import { Component, Input, OnInit, OnDestroy, input, inject } from '@angular/core';

import {
  AdminModalDisplay,
  UpdateVehiclesActions,
  VehicleStatusAction
} from '../../../enums/inventory-management.enum';
import { ChangeStatusModalDirective } from '../change-status-modal.directive';
import { Subject } from 'rxjs';
import {
  AuctionItemSell,
  AuctionService, InventoryManagementSearchResult,
  InventoryService,
  LaunchDarklyService,
  YellowLight
} from 'remarketing-angular-library';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  UpdateResultSummary,
  UpdateVehiclesAction,
  VehicleUpdateSummary
} from '../../../interfaces/update-vehicles-action.interface';
import { Store } from '@ngxs/store';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ErrorIconComponent, SpinnerComponent } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';
import { take, takeUntil } from 'rxjs/operators';
import { groupBy } from 'lodash';
import {
  MultiSelectorDropdownComponent
} from '../../../../sell/lib/posting/multi-selector-dropdown/multi-selector-dropdown.component';
import { UpdateSelectedVehiclesService } from '../../../services/update-selected-vehicles.service';


@Component({
  selector: 'app-update-selected-modal',
  imports: [
    MultiSelectorDropdownComponent,
    SpinnerComponent,
    ErrorIconComponent,
    CommonModule,
    TranslocoDirective,
    ReactiveFormsModule
  ],
  templateUrl: './update-selected-modal.component.html',
  styleUrl: './update-selected-modal.component.scss'
})
export class UpdateSelectedModalComponent extends ChangeStatusModalDirective implements OnInit {

  protected readonly multipleTenantsSelected$ = input<boolean>();

  readonly AdminModalDisplayEnum = AdminModalDisplay;
  private readonly UPDATE_ACTION_CONTROL_LABEL = 'updateAction';
  private readonly YELLOW_LIGHT_CONTROL_LABEL = 'yellowLight';
  private readonly DEFAULT = 'default';
  private readonly MULTIPLE_TENANT_ERROR =
    'You have selected vehicles from multiple tenants. Please limit selections to one tenant at a time.';
  private activeAndHoldVehicles: InventoryManagementSearchResult[];
  protected updateVehiclesForm: UntypedFormGroup;
  private vehiclesEligibleForMarkArrived: InventoryManagementSearchResult[];
  private totalVehiclesInTransit;
  private soldVehiclesProceedsHeld: InventoryManagementSearchResult[]; // eligible for release proceeds
  private soldVehiclesProceedsReleased: InventoryManagementSearchResult[]; // eligible for reverse release proceeds

  protected defaultVehicleAction = {
    name: 'Select',
    value: UpdateVehiclesActions.DEFAULT
  } as UpdateVehiclesAction;

  private activeAndHoldVehicleCount: number;
  protected selectedVehicleCount: number;
  private soldVehicleProceedsHeldCount: number;
  private soldVehicleProceedsReleasedCount: number;

  protected possibleUpdateActions: UpdateVehiclesAction[] = [];
  protected yellowLightOptions: YellowLight[];
  protected vehicleStatusList: UpdateVehiclesAction[] = [];
  private groupAuctionItemsByStatus; // Vehicle Status : InventoryManagementSearchResult[]
  private groupAuctionItemsByAuctionItemUuid; // auctionItemUuid: InventoryManagementSearchResult
  private groupAuctionItemsByInTransit;
  protected yellowLightOptionsSuccessfullyRetrieved: boolean;
  private isYellowLightRetrievalError: boolean;
  protected isLoading: boolean;
  protected contentDisplay: string;
  protected templatePunctuation = {
    period: '.',
    parenthesis: '(',
    closingParenthesis: ')',
    colonWithSpace: ': '
  };

  private updateSummary: UpdateResultSummary;

  // Header display
  private updateHeaderLabelSingular: string;
  private updateHeaderLabelPlural: string;
  protected updatedHeaderText: string;

  // Success display
  private successLabelSingular: string;
  private successLabelPlural: string;
  protected successMessage: string;
  protected successVins: string;

  // Failure display
  protected failureLabelSingular: string;
  protected failureLabelPlural: string;
  private failureVins: string;
  protected updateErrorDisplays: UpdateErrorDisplay[];

  // Error Display
  protected errorMessage: string;
  protected errorHeaderText: string;

  // Single Vehicle Text
  private textUpdateSingleVehicleHeader: string;
  private textSelectingYesDisclaimerSingleVehicle: string;

  // Mulitple Vehicle Text
  private textUpdateMultipleVehiclesHeader: string;
  private textSelectingYesDisclaimerMultipleVehicles: string;

  private updateSelectedVehiclesService: UpdateSelectedVehiclesService;
  private readonly inventoryService: InventoryService;

  constructor() {
    super();
    this.updateSelectedVehiclesService = inject(UpdateSelectedVehiclesService);
    this.inventoryService = inject(InventoryService);
  }

  ngOnInit(): void {
    this.getTranslations();
    this.setDefaultValues();
  }

  setDefaultValues(): void {
    this.selectedVehicleCount = this.selectedVehicles.length;
    this.activeAndHoldVehicles = this.selectedVehicles?.filter(
      vehicle => vehicle.auctionItemStatus === 'HOLD' || vehicle.auctionItemStatus === 'ACTIVE'
    );
    this.activeAndHoldVehicleCount = this.activeAndHoldVehicles?.length;

    // maintain alphabetic order
    this.setUpYellowLightDisclosures();
    this.setUpInTransitVehicleData();
    this.setUpReleaseProceeds();
    this.setUpVehicleStatusChange();

    this.updateVehiclesForm = new UntypedFormGroup({
      updateAction: new UntypedFormControl(UpdateVehiclesActions.DEFAULT),
      yellowLight: new UntypedFormControl(this.DEFAULT),
      vehicleStatus: new UntypedFormControl(this.DEFAULT)
    });
    this.trackActionControlChanges();
    this.trackYellowLightControlChanges();

    if (this.multipleTenantsSelected$()) {
      this.errorMessage = this.MULTIPLE_TENANT_ERROR;
      this.contentDisplay = AdminModalDisplay.ERROR_TEMPLATE;
    } else {
      this.contentDisplay = AdminModalDisplay.FORM_TEMPLATE;
    }
  }

  trackActionControlChanges() {
    const updateActionControl = this.updateVehiclesForm.get(this.UPDATE_ACTION_CONTROL_LABEL);
    updateActionControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(action => {
      if (action === UpdateVehiclesActions.DEFAULT) {
        this.removeErrorFlags();
      }
      this.loadOptionsForSelectedAction();
    });
  }

  trackYellowLightControlChanges() {
    const yellowLightDropdownControl = this.updateVehiclesForm.get(this.YELLOW_LIGHT_CONTROL_LABEL);
    yellowLightDropdownControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(yellowLight => {
      this.setAsSelectedYellowLight(yellowLight);
    });
  }

  setAsSelectedYellowLight(yellowLightOption: YellowLight) {
    this.yellowLightOptions.forEach(option =>
      option.disclosureUuid === yellowLightOption.disclosureUuid ? (option.selected = true) : (option.selected = false)
    );
  }

  removeErrorFlags() {
    this.isYellowLightRetrievalError = false;
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('sellModule.manageInventory.actionListModal.updateSelectedModal')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.textUpdateSingleVehicleHeader = translate.textUpdateVehicle;
        this.textUpdateMultipleVehiclesHeader = translate.textUpdateVehicles;
        this.textSelectingYesDisclaimerSingleVehicle = translate.textSelectingYesDisclaimerSingleVehicle;
        this.textSelectingYesDisclaimerMultipleVehicles = translate.textSelectingYesDisclaimerMultipleVehicles;
        this.successLabelSingular = translate.textSuccessLabelSingular;
        this.successLabelPlural = translate.textSuccessLabelPlural;
        this.failureLabelSingular = translate.textFailureLabelSingular;
        this.failureLabelPlural = translate.textFailureLabelPlural;
        this.updateHeaderLabelSingular = translate.textVehicleUpdated;
        this.updateHeaderLabelPlural = translate.textVehiclesUpdated;
        this.errorMessage = translate.textErrorMessage;
        this.errorHeaderText = translate.textErrorHeader;
      });
  }

  loadOptionsForSelectedAction() {
    if (this.isYellowLightAction && !this.isYellowLightOptionsLoaded) {
      this.getYellowLightOptions();
    }
  }

  getYellowLightOptions() {
    const tenantUuid = this.selectedVehicleTenant;
    this.inventoryService
      .getDisclosures(tenantUuid)
      .pipe(take(1))
      .subscribe(
        (yellowLightDtos: YellowLight[]) => {
          this.setAllYellowLightOptionsToFalse(yellowLightDtos);
          this.yellowLightOptions = this.sortYellowLightDescriptions(yellowLightDtos);
        },
        error => {
          this.setYellowLightErrorFlags();
          console.log('Error retrieving Yellow Light Disclosure options. Error: ', error);
        },
        () => {
          if (!this.isYellowLightOptionsLoaded) {
            this.setYellowLightErrorFlags();
          } else {
            this.setYellowLightRetrievalSuccessFlags();
          }
        }
      );
  }

  setAllYellowLightOptionsToFalse(yellowLights: YellowLight[]) {
    yellowLights?.forEach(yellowLight => (yellowLight.selected = false));
  }

  setYellowLightRetrievalSuccessFlags() {
    this.yellowLightOptionsSuccessfullyRetrieved = true;
    this.isYellowLightRetrievalError = false;
  }

  setYellowLightErrorFlags() {
    this.yellowLightOptionsSuccessfullyRetrieved = false;
    this.isYellowLightRetrievalError = true;
  }

  public sortYellowLightDescriptions(yellowLightDtos) {
    return yellowLightDtos?.sort((a, b) => {
      if (a.disclosureDescription.toUpperCase() < b.disclosureDescription.toUpperCase()) {
        return -1;
      } else {
        return a.disclosureDescription.toUpperCase() > b.disclosureDescription.toUpperCase() ? 1 : 0;
      }
    });
  }

  updateVehicles() {
    if (this.isAdmin) {
      if (this.isYellowLightAction) {
        this.isLoading = true;
        const selectedYellowLights = this.yellowLightOptions.filter(option => option.selected === true);
        const updateYellowLightDisclosureDto = this.updateSelectedVehiclesService.buildYellowLightDisclosuresDto(
          this.activeAndHoldVehicles,
          selectedYellowLights
        );
        let formattedResponse;
        this.inventoryService.updateYellowLightDisclosures(updateYellowLightDisclosureDto).subscribe(
          response => {
            formattedResponse = this.updateSelectedVehiclesService.formatUpdateYellowLightDisclosureResponseDto(
              response
            );
          },
          error => {
            console.log('Error while updating yellow light disclosures. {}', error);
            this.setUpdateErrorData();
          },
          () => {
            this.setUpdateHappyPathData(formattedResponse);
          }
        );
      }
      if (this.isVehicleStatusChangeAction) {
        this.isLoading = true;
        const updateToStatus = this.updateVehiclesForm.get('vehicleStatus').value;
        const auctionItemUuidsToUpdate = this.getVehicleStatusChangeAuctionItemUuids(updateToStatus);
        let format;
        this.groupAuctionItemsByAuctionItemUuid = groupBy(this.activeAndHoldVehicles, 'vehicleUuid');
        this.auctionService.updateVehicleStatus(auctionItemUuidsToUpdate, updateToStatus).subscribe(
          response => {
            format = this.updateSelectedVehiclesService.formatUpdateVehicleStatusResponse(
              response,
              this.groupAuctionItemsByAuctionItemUuid
            );
          },
          error => {
            console.log('Error while updating vehicle status. {}', error);
            this.setUpdateErrorData();
          },
          () => {
            this.setUpdateHappyPathData(format);
          }
        );
      }
      if (this.isVehicleMarkArrivedAction) {
        this.isLoading = true;
        const auctionItemUuidsToUpdate = this.groupAuctionItemsByInTransit['true']
          ? this.groupAuctionItemsByInTransit['true'].map(auctionItem => auctionItem.vehicleUuid)
          : [];
        let format;
        this.groupAuctionItemsByAuctionItemUuid = groupBy(this.vehiclesEligibleForMarkArrived, 'vehicleUuid');
        this.auctionService.markVehicleArrived(auctionItemUuidsToUpdate).subscribe(
          response => {
            format = this.updateSelectedVehiclesService.formatUpdateVehicleStatusResponse(
              response,
              this.groupAuctionItemsByAuctionItemUuid
            );
          },
          error => {
            console.log('Error while updating vehicle status. {}', error);
            this.setUpdateErrorData();
          },
          () => {
            this.setUpdateHappyPathData(format);
          }
        );
      }
      if (this.isReleaseProceeds) {
        this.isLoading = true;
        let formattedResponse;
        const payload = this.updateSelectedVehiclesService.buildProceedsReleasePayload(this.soldVehiclesProceedsHeld);
        this.inventoryService.releaseProceeds(payload).subscribe(
          response => {
            formattedResponse = this.updateSelectedVehiclesService.formatProceeds(response);
          },
          error => {
            console.log('Error releasing proceeds. {}', error);
            this.setUpdateErrorData();
          },
          () => {
            this.setUpdateHappyPathData(formattedResponse);
          }
        );
      }
      if (this.isReverseReleaseProceeds) {
        this.isLoading = true;
        let formattedResponse;
        const payload = this.updateSelectedVehiclesService.buildProceedsReleasePayload(
          this.soldVehiclesProceedsReleased
        );
        this.inventoryService.reverseReleaseProceeds(payload).subscribe(
          response => {
            formattedResponse = this.updateSelectedVehiclesService.formatProceeds(response);
          },
          error => {
            console.log('Error reversing release proceeds. {}', error);
            this.setUpdateErrorData();
          },
          () => {
            this.setUpdateHappyPathData(formattedResponse);
          }
        );
      }
    } else {
      console.log('User does not have proper credentials to update vehicles.');
    }
  }

  /* Get auction items uuids for vehicle status update
   * based on the selected updateToStatus
   * */
  getVehicleStatusChangeAuctionItemUuids(updateToStatus) {
    const holdUuids = this.groupAuctionItemsByStatus['HOLD']
      ? this.groupAuctionItemsByStatus['HOLD'].map(auctionItem => auctionItem.vehicleUuid)
      : [];
    const activeUuids = this.groupAuctionItemsByStatus['ACTIVE']
      ? this.groupAuctionItemsByStatus['ACTIVE'].map(auctionItem => auctionItem.vehicleUuid)
      : [];
    let auctionItemUuidsToUpdate = [];
    if (updateToStatus == VehicleStatusAction.ACTIVE) {
      auctionItemUuidsToUpdate = auctionItemUuidsToUpdate.concat(holdUuids);
    }
    if (updateToStatus == VehicleStatusAction.HOLD) {
      auctionItemUuidsToUpdate = auctionItemUuidsToUpdate.concat(activeUuids);
    }
    if (updateToStatus == VehicleStatusAction.REMOVE) {
      auctionItemUuidsToUpdate = auctionItemUuidsToUpdate.concat(activeUuids, holdUuids);
    }
    return auctionItemUuidsToUpdate;
  }

  private setUpdateHappyPathData(formattedResponse: UpdateResultSummary) {
    this.isLoading = false;
    this.updateSummary = formattedResponse;
    this.updatedHeaderText = this.setUpdatedVehiclesHeader();
    this.contentDisplay = AdminModalDisplay.RESULTS_TEMPLATE;
    this.createSuccessSummary();
    this.createFailureSummary();
  }

  private setUpdateErrorData() {
    this.isLoading = false;
    this.updatedHeaderText = this.errorHeaderText;
    this.contentDisplay = AdminModalDisplay.ERROR_TEMPLATE;
    const formattedResponse = {
      successes: [],
      failures: []
    } as UpdateResultSummary;
    this.updateSummary = formattedResponse;
  }

  setUpdatedVehiclesHeader(): string {
    const successNumber = this.updateSummary?.successes?.length;
    let formattedHeaderText = '';
    if (successNumber === undefined) {
      formattedHeaderText = `0 ${this.updateHeaderLabelPlural}`;
    } else if (successNumber && successNumber === 1) {
      formattedHeaderText = `${successNumber} ${this.updateHeaderLabelSingular}`;
    } else {
      formattedHeaderText = `${successNumber} ${this.updateHeaderLabelPlural}`;
    }
    return formattedHeaderText;
  }

  createSuccessSummary() {
    this.successVins = '';
    this.successMessage = '';

    if (this.updateSummary?.successes?.length > 0) {
      this.successVins = this.stringifyAndformatVinsCommaSeperated(this.updateSummary.successes);

      if (this.isSingleSuccess) {
        this.successMessage = `${this.updateSummary.successes.length} ${this.successLabelSingular}`;
      } else {
        this.successMessage = `${this.updateSummary.successes.length} ${this.successLabelPlural}`;
      }
    }
  }

  createFailureSummary() {
    this.failureVins = '';
    if (this.updateSummary?.failures?.length > 0) {
      const uniqueErrorDisplays = this.buildUniqueUpdateErrorDisplays(this.updateSummary.failures);
      this.updateErrorDisplays = this.addVinsToUpdateErrorDisplayByCode(
        uniqueErrorDisplays,
        this.updateSummary.failures
      );
    }
  }

  buildUniqueUpdateErrorDisplays(failures: VehicleUpdateSummary[]): UpdateErrorDisplay[] {
    const uniqueErrorCodes = [];
    if (failures?.length) {
      failures.forEach(failure => {
        if (!uniqueErrorCodes.includes(failure.errors[0].errorCode)) {
          uniqueErrorCodes.push(failure.errors[0].errorCode);
        }
      });
      return this.buildPartialUpdateErrorDisplay(uniqueErrorCodes, failures);
    } else {
      console.log('No failures to build error displays');
    }
  }

  buildPartialUpdateErrorDisplay(uniqueErrorCodes: string[], failures: VehicleUpdateSummary[]): UpdateErrorDisplay[] {
    const partialUpdateErrorDisplays = [];
    if (uniqueErrorCodes?.length > 0) {
      uniqueErrorCodes.forEach(errorCode => {
        const failure = failures.find(failure => failure.errors[0].errorCode === errorCode);
        const failureMessage =
          failure?.errors[0]?.errorDescription?.length > 0
            ? failure.errors[0].errorDescription
            : failure?.errors[0].errorMessage
              ? failure?.errors[0].errorMessage
              : 'Unknown Error : Error Not Mapped';
        const updateErrorDisplay = {
          errorCode: errorCode,
          errorMessage: failureMessage,
          vins: [],
          stringifiedVins: ''
        } as UpdateErrorDisplay;
        partialUpdateErrorDisplays.push(updateErrorDisplay);
      });
    }
    return partialUpdateErrorDisplays;
  }

  addVinsToUpdateErrorDisplayByCode(
    uniqueUpdateErrorDisplays: UpdateErrorDisplay[],
    failures: VehicleUpdateSummary[]
  ): UpdateErrorDisplay[] {
    const uniqueErrorDisplays = uniqueUpdateErrorDisplays;
    uniqueErrorDisplays.forEach(display => {
      failures.forEach(failure => {
        if (display.errorCode === failure.errors[0].errorCode) {
          display.vins.push(failure.vin);
        }
      });
    });
    uniqueErrorDisplays.forEach(display => {
      display.stringifiedVins = display.vins.join(', ');
    });
    return uniqueErrorDisplays;
  }

  stringifyAndformatVinsCommaSeperated(vehicleSummaries: VehicleUpdateSummary[]): string {
    const vins = [];
    if (vehicleSummaries?.length) {
      vehicleSummaries.forEach(vehicle => {
        vins.push(vehicle.vin);
      });
      return vins.join(', ');
    } else {
      return '';
    }
  }

  get isYellowLightOptionsLoaded() {
    return this.yellowLightOptions?.length > 0;
  }

  get isYellowLightAction() {
    return this.updateVehiclesForm.get('updateAction').value === UpdateVehiclesActions.DISCLOSURES_YELLOW_LIGHTS;
  }

  get isVehicleStatusChangeAction() {
    return this.updateVehiclesForm.get('updateAction').value === UpdateVehiclesActions.VEHICLE_STATUS_CHANGE;
  }

  get isVehicleMarkArrivedAction() {
    return this.updateVehiclesForm.get('updateAction').value === UpdateVehiclesActions.MARK_ARRIVED;
  }

  get isReverseReleaseProceeds() {
    return this.updateVehiclesForm.get('updateAction').value === UpdateVehiclesActions.REVERSE_RELEASE_PROCEEDS;
  }

  get isReleaseProceeds() {
    return this.updateVehiclesForm.get('updateAction').value === UpdateVehiclesActions.RELEASE_PROCEEDS;
  }

  get isYellowLightError() {
    return this.isYellowLightRetrievalError && this.isYellowLightAction;
  }

  get isVehicleStatusError() {
    return (
      this.isVehicleStatusChangeAction &&
      this.updateVehiclesForm.get('vehicleStatus').value == UpdateVehiclesActions.DEFAULT
    );
  }

  get isVehicleStatusSelected() {
    return this.updateVehiclesForm.get('vehicleStatus').value != UpdateVehiclesActions.DEFAULT;
  }
  get isYellowLightSelectionReady() {
    const yellowLightCount = this.yellowLightOptions?.filter(option => option.selected === true).length;
    return yellowLightCount > 0;
  }

  get headerText() {
    return this.isMultipleVehicles ? this.textUpdateMultipleVehiclesHeader : this.textUpdateSingleVehicleHeader;
  }

  get selectingYesDisclaimerText() {
    return this.isMultipleVehicles
      ? this.textSelectingYesDisclaimerMultipleVehicles
      : this.textSelectingYesDisclaimerSingleVehicle;
  }

  get isMultipleVehicles() {
    return this.selectedVehicles?.length > 1;
  }

  get isSuccessVins() {
    return this.updateSummary?.successes?.length > 0;
  }

  get isSingleSuccess() {
    return this.updateSummary?.successes?.length === 1;
  }

  get isFailureVins() {
    return this.updateSummary?.failures?.length > 0;
  }

  get selectedVehicleTenant() {
    return this.selectedVehicles[0].auctionItemTenantUuid;
  }

  get isMultipleYellowLightsEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-yellowLightMultiSelectEnabled', true);
  }

  get isAdminStatusChangeEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-adminStatusChangeEnabled', true);
  }

  get isReleaseProceedsEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('adminReleaseProceedsEnabled', true);
  }

  // Option should only be available if sold vehicles are selected,
  // and depending on the proceeds status of each vehicle.
  setUpReleaseProceeds() {
    this.soldVehiclesProceedsHeld = this.selectedVehicles?.filter(
      vehicle => vehicle.auctionItemStatus === 'SOLD' && vehicle.holdProceedsTypeCode === 'H'
    );
    this.soldVehiclesProceedsReleased = this.selectedVehicles?.filter(
      vehicle => vehicle.auctionItemStatus === 'SOLD' && vehicle.holdProceedsTypeCode === 'R'
    );

    this.soldVehicleProceedsHeldCount = this.soldVehiclesProceedsHeld.length;
    this.soldVehicleProceedsReleasedCount = this.soldVehiclesProceedsReleased.length;

    if (this.isReleaseProceedsEnabled) {
      if (this.soldVehicleProceedsHeldCount) {
        this.possibleUpdateActions.push({
          name: 'Release Proceeds',
          value: UpdateVehiclesActions.RELEASE_PROCEEDS
        } as UpdateVehiclesAction);
      }
      if (this.soldVehicleProceedsReleasedCount) {
        this.possibleUpdateActions.push({
          name: 'Reverse Release Proceeds',
          value: UpdateVehiclesActions.REVERSE_RELEASE_PROCEEDS
        } as UpdateVehiclesAction);
      }
    }
  }

  setUpYellowLightDisclosures() {
    if (this.isMultipleYellowLightsEnabled && this.activeAndHoldVehicleCount) {
      this.possibleUpdateActions.push({
        name: 'Disclosures/Yellow-Lights',
        value: UpdateVehiclesActions.DISCLOSURES_YELLOW_LIGHTS
      } as UpdateVehiclesAction);
    }
  }

  get isAdminMarkArrivedEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-adminMarkArrivedEnabled', true);
  }

  setUpVehicleStatusChange() {
    this.groupAuctionItemsByStatus = groupBy(this.activeAndHoldVehicles, 'auctionItemStatus');
    // should only show if active/hold vehicle is selected
    if (this.isAdminStatusChangeEnabled && this.activeAndHoldVehicleCount) {
      // add status change option
      this.possibleUpdateActions.push({
        name: 'Vehicle Status Change',
        value: UpdateVehiclesActions.VEHICLE_STATUS_CHANGE
      });

      // determine selected vehicle status
      const selectedVehicleStatusList = Object.keys(this.groupAuctionItemsByStatus);
      const holdSelected = selectedVehicleStatusList.includes('HOLD');
      const activeSelected = selectedVehicleStatusList.includes('ACTIVE');
      const mappedStatus = [];
      // populate status drop down based on selected vehicles status
      if (activeSelected && holdSelected) {
        mappedStatus.push(
          { name: 'Move to Active', value: VehicleStatusAction.ACTIVE },
          { name: 'Move to Hold', value: VehicleStatusAction.HOLD },
          { name: 'Remove', value: VehicleStatusAction.REMOVE }
        );
      } else if (activeSelected) {
        mappedStatus.push(
          { name: 'Move to Hold', value: VehicleStatusAction.HOLD },
          { name: 'Remove', value: VehicleStatusAction.REMOVE }
        );
      } else if (holdSelected) {
        mappedStatus.push(
          { name: 'Move to Active', value: VehicleStatusAction.ACTIVE },
          { name: 'Remove', value: VehicleStatusAction.REMOVE }
        );
      }
      this.vehicleStatusList = mappedStatus;
    }
  }

  get vehicleStatusChangeTotal(): number {
    const statusChange = this.updateVehiclesForm.get('vehicleStatus').value;
    const holdCount = this.groupAuctionItemsByStatus['HOLD']?.length || 0;
    const activeCount = this.groupAuctionItemsByStatus['ACTIVE']?.length || 0;
    let total = 0;
    if (statusChange == VehicleStatusAction.ACTIVE) {
      total = holdCount;
    }
    if (statusChange == VehicleStatusAction.HOLD) {
      total = activeCount;
    }
    if (statusChange == VehicleStatusAction.REMOVE) {
      total = activeCount + holdCount;
    }
    return total;
  }

  get messageForVehicleNumberUpdate(): string {
    const selectedAction = this.updateVehiclesForm.get('updateAction').value;
    let total;
    switch (selectedAction) {
      case UpdateVehiclesActions.DISCLOSURES_YELLOW_LIGHTS:
        if (this.isYellowLightSelectionReady) {
          total = this.activeAndHoldVehicleCount;
        }
        break;
      case UpdateVehiclesActions.VEHICLE_STATUS_CHANGE:
        if (this.isVehicleStatusSelected) {
          total = this.vehicleStatusChangeTotal;
        }
        break;
      case UpdateVehiclesActions.MARK_ARRIVED:
        total = this.totalVehiclesInTransit;
        break;
      case UpdateVehiclesActions.RELEASE_PROCEEDS:
        total = this.soldVehicleProceedsHeldCount;
        break;
      case UpdateVehiclesActions.REVERSE_RELEASE_PROCEEDS:
        total = this.soldVehicleProceedsReleasedCount;
        break;
    }
    if (total === 1) {
      return 'This change will be applied to ' + total + ' vehicle.';
    } else if (total > 1 || total == 0) {
      return 'This change will be applied to ' + total + ' vehicles.';
    }
    return '';
  }

  setUpInTransitVehicleData() {
    this.vehiclesEligibleForMarkArrived = this.selectedVehicles?.filter(vehicle => vehicle.inTransit === true);
    this.groupAuctionItemsByInTransit = groupBy(this.vehiclesEligibleForMarkArrived, 'inTransit');
    this.totalVehiclesInTransit = this.groupAuctionItemsByInTransit['true']?.length || 0;
    if (this.isAdminMarkArrivedEnabled && this.totalVehiclesInTransit > 0) {
      this.possibleUpdateActions.push({
        name: 'Mark Arrived',
        value: UpdateVehiclesActions.MARK_ARRIVED
      });
    }
  }
}

export class UpdateErrorDisplay {
  errorCode: string;
  errorMessage: string;
  vins: string[];
  stringifiedVins: string;
}
